import { zodResolver } from "@hookform/resolvers/zod";
import { Gauge, Pencil } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ActionCell } from "@/components/shared/action-cell";
import { ConfigFormDialog } from "@/components/shared/config-form-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  type ConstraintLevel,
  useConstraintLevels,
  useUpdateConstraintLevel,
} from "@/hooks/use-constraints";
import {
  type ConstraintLevelUpdateInput,
  constraintLevelUpdateSchema,
} from "@/schemas/constraint-levels";

/**
 * Settings → Config → Constraint ratings: the five-rung familiarity scale
 * people are rated against on constraints.
 *
 * **Separate from Performance ratings, and that is the entire point of the
 * story.** Reusing the performance rungs would say "does not meet
 * expectations" about an industry someone has simply never worked in. Rung 1
 * here means *limited familiarity*; no familiarity at all is recorded by not
 * giving the person that constraint, never by a low score.
 *
 * Names and definitions are editable, the rungs themselves are fixed — same
 * posture as the performance scale. Editing never rewrites a recorded rating,
 * because ratings store the ordinal rather than a link to this row.
 */
export function SettingsConstraintRatingsPage() {
  const { data: scale = [], isLoading, isError, error, refetch } = useConstraintLevels();
  const [editing, setEditing] = useState<ConstraintLevel | null>(null);

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load constraint ratings"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-muted-foreground">
        The scale a person's familiarity with a constraint is rated on. This is{" "}
        <span className="font-medium text-foreground">not</span> the performance scale — a 1 here
        means limited familiarity with something, not that someone is falling short. Somebody with
        no familiarity at all simply doesn't have that constraint on their profile; there is no
        zero. Rename a rating or rewrite its definition at any time: recorded ratings keep the
        number they were given, so nothing is rewritten underneath anyone.
      </p>

      <DataTable
        headers={[
          { label: "Rating", className: "w-24" },
          { label: "Name" },
          { label: "Definition" },
          { label: "Actions", className: "text-right" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Gauge}
            title="No constraint ratings"
            description="The five rungs are seeded on server startup."
          />
        }
      >
        {scale.map((row) => (
          <tr
            key={row.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3 font-medium tabular-nums">{row.ordinal}</td>
            <td className="px-4 py-3 font-medium">{row.name}</td>
            <td className="px-4 py-3 text-muted-foreground">{row.description ?? "—"}</td>
            <td className="px-4 py-3 text-right">
              <ActionCell
                items={[{ icon: Pencil, label: "Edit", onClick: () => setEditing(row) }]}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      {editing && (
        <ConstraintRatingDialog open onOpenChange={() => setEditing(null)} level={editing} />
      )}
    </div>
  );
}

function ConstraintRatingDialog({
  open,
  onOpenChange,
  level,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  level: ConstraintLevel;
}) {
  const update = useUpdateConstraintLevel();
  const form = useForm<ConstraintLevelUpdateInput>({
    resolver: zodResolver(constraintLevelUpdateSchema),
    defaultValues: {
      ordinal: level.ordinal,
      name: level.name,
      description: level.description ?? "",
    },
  });

  async function onSubmit(values: ConstraintLevelUpdateInput) {
    await update.mutateAsync(values);
    onOpenChange(false);
  }

  return (
    <ConfigFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={<>Edit rating {level.ordinal}</>}
      description={
        <>
          Rename this rung or rewrite what it means. Everyone already rated {level.ordinal} keeps
          that rating.
        </>
      }
      form={form}
      onSubmit={onSubmit}
      submitLabel="Save"
      pending={update.isPending}
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Definition</FormLabel>
            <FormControl>
              <Textarea
                rows={3}
                placeholder="What this level of familiarity looks like."
                {...field}
                value={field.value ?? ""}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </ConfigFormDialog>
  );
}
